import * as React from 'react';
import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Backdrop from '@mui/material/Backdrop';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Typography from '@mui/material/Typography';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { useTranslation } from "react-i18next";
import AppTheme from '../theme/AppTheme';
import DropoffForm from '../components/DropoffForm';
import Review from '../components/Review';
import { useAppDispatch, useAppSelector } from "../hooks/redux-hooks";
import { Popover, Paper } from '@mui/material';
import { styled, FormControlLabel } from '@mui/material';
import Radio from '@mui/material/Radio';
import { placeOrder, resetOrderStatus, updateMyOrderById, getFutureOrdersByCustomerId } from "../slices/authSlice";
import { ORDER_STATUS, SERVICE_TYPE, vehicleType } from '../constants';
import { showNotification, NotificationType } from "../slices/notificationSlice";
import miniVanImage from '../assets/miniVan.png';
import luxurySuvImage from '../assets/luxurySuv.jpg';
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import dayjs from 'dayjs';
import * as consts from '../constants';
import PaymentMethodDialog from '../components/PaymentMethodDialog';
import { keyframes } from '@mui/system';

const FormGrid = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const dotPulse = keyframes`
  0%, 80%, 100% {
    opacity: 0.2;
    transform: scale(0.7);
  }
  40% {
    opacity: 1;
    transform: scale(1);
  }
`;

interface DropoffProps {
  isEdit?: boolean;
}

type SerializedQrCodeFile = {
  name: string;
  type: string;
  size: number;
  dataUrl: string;
};

const isSerializedQrCodeFile = (value: unknown): value is SerializedQrCodeFile => {
  if (!value || typeof value !== "object") return false;
  const file = value as SerializedQrCodeFile;
  return Boolean(file.name && file.type && file.dataUrl);
};

const serializeQrCodeFile = (file: File): Promise<SerializedQrCodeFile> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve({
        name: file.name,
        type: file.type,
        size: file.size,
        dataUrl: String(reader.result || ""),
      });
    };
    reader.onerror = () => reject(new Error("Failed to read qrcode file"));
    reader.readAsDataURL(file);
  });

const deserializeQrCodeFile = async (
  serialized: SerializedQrCodeFile
): Promise<File> => {
  const response = await fetch(serialized.dataUrl);
  const blob = await response.blob();
  return new File([blob], serialized.name, { type: serialized.type || blob.type });
};

const DropOff = ({ isEdit = false, ...props }: DropoffProps) => {
  const [vehicle, setVehicle] = React.useState<vehicleType>(vehicleType.MINIVAN);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const basicUserInfo = useAppSelector((state) => state.auth.basicUserInfo);
  const orderStatus = useAppSelector((state) => state.auth.orderStatus);
  const orderEmailRef = React.useRef("");
  const futureOrders = useAppSelector((state) => state.auth.orders);
  const [isSaveOrderClicked, setIsSaveOrderClicked] = useState(false);

  // 从路由状态中获取订单信息
  const passedOrderInfo = location.state?.orderInfo;
  const processedOrderInfo = React.useMemo(() => {
    if (!passedOrderInfo) return null;

    let flightDateTime = null;
    let useDateTime = null;
    if (passedOrderInfo.flight_time) {
      // flightDateTime = dayjs(passedOrderInfo.flight_time);
      flightDateTime = dayjs(passedOrderInfo.flight_time).tz('America/Los_Angeles');
    }
    if (passedOrderInfo.use_time) {
      // useDateTime = dayjs(passedOrderInfo.use_time);
      useDateTime = dayjs(passedOrderInfo.use_time).tz('America/Los_Angeles');
    }

    // const parseCount = (value: any, fallback: number) => {
    //   const parsed = typeof value === "number" ? value : Number(value);
    //   return Number.isFinite(parsed) ? parsed : fallback;
    // };

    return {
      ...passedOrderInfo,
      // passengers: parseCount(passedOrderInfo.passengers, 1),
      // luggages: parseCount(passedOrderInfo.luggages, 1),
      // carry_on: parseCount(passedOrderInfo.carry_on, 1),
      // 如果是编辑模式，从 use_time 反向计算
      // useDate: isEdit && useDateTime ?
      //   dayjs(useDateTime) :
      //   (passedOrderInfo.useDate ?
      //     (dayjs.isDayjs(passedOrderInfo.useDate) ? passedOrderInfo.useDate : dayjs(passedOrderInfo.useDate)) :
      //     dayjs()
      //   ),
      // useTime: isEdit && useDateTime ?
      //   dayjs(useDateTime) :
      //   (passedOrderInfo.useTime ?
      //     (dayjs.isDayjs(passedOrderInfo.useTime) ? passedOrderInfo.useTime : dayjs(passedOrderInfo.useTime)) :
      //     dayjs()
      //   ),
      // // 如果是编辑模式，从 flight_time 反向计算
      // flightDate: isEdit && flightDateTime ?
      //   dayjs(flightDateTime) :
      //   (passedOrderInfo.flightDate ?
      //     (dayjs.isDayjs(passedOrderInfo.flightDate) ? passedOrderInfo.flightDate : dayjs(passedOrderInfo.flightDate)) :
      //     dayjs()
      //   ),
      // flightTime: isEdit && flightDateTime ?
      //   dayjs(flightDateTime) :
      //   (passedOrderInfo.flightTime ?
      //     (dayjs.isDayjs(passedOrderInfo.flightTime) ? passedOrderInfo.flightTime : dayjs(passedOrderInfo.flightTime)) :
      //     dayjs()
      //   ),
      useDate:
        isEdit && useDateTime
          ? dayjs(useDateTime)//.tz("America/Los_Angeles")
          : passedOrderInfo.useDate
          ? dayjs.isDayjs(passedOrderInfo.useDate)
            ? passedOrderInfo.useDate
            : dayjs(passedOrderInfo.useDate).tz("America/Los_Angeles")
          : dayjs().tz("America/Los_Angeles"),
      useTime:
        isEdit && useDateTime
          ? dayjs(useDateTime)//.tz("America/Los_Angeles")
          : passedOrderInfo.useTime
          ? dayjs.isDayjs(passedOrderInfo.useTime)
            ? passedOrderInfo.useTime
            : dayjs(passedOrderInfo.useTime).tz("America/Los_Angeles")
          : dayjs().tz("America/Los_Angeles"),
      // 如果是编辑模式，从 flight_time 反向计算
      flightDate:
        isEdit && flightDateTime
          ? dayjs(flightDateTime)//.tz("America/Los_Angeles")
          : passedOrderInfo.flightDate
          ? dayjs.isDayjs(passedOrderInfo.flightDate)
            ? passedOrderInfo.flightDate
            : dayjs(passedOrderInfo.flightDate).tz("America/Los_Angeles")
          : dayjs().tz("America/Los_Angeles"),
      flightTime:
        isEdit && flightDateTime
          ? dayjs(flightDateTime)//.tz("America/Los_Angeles")
          : passedOrderInfo.flightTime
          ? dayjs.isDayjs(passedOrderInfo.flightTime)
            ? passedOrderInfo.flightTime
            : dayjs(passedOrderInfo.flightTime).tz("America/Los_Angeles")
          : dayjs().tz("America/Los_Angeles"),
    };
  }, [passedOrderInfo, isEdit]);

    // 如果有传入的订单信息，设置车型
    useEffect(() => {
      if (processedOrderInfo && processedOrderInfo.vehicle_type) {
        setVehicle(processedOrderInfo.vehicle_type);
      }
    }, [processedOrderInfo]);

    const steps = [t(isEdit ? "editDropoffInfo" : "fillInDropOffInfo"), t("reviewOrder")];
  const [activeStep, setActiveStep] = React.useState(0);
  // const handleNext = () => {
  //   // setActiveStep(activeStep + 1);
  //   setSubmitRequested(true);
  //   if (activeStep == 1) {
  //     handlePlaceOrder(orderInfo);
  //     // send to server
  //     //setActiveStep(activeStep + 1);
  //   }
  // };
  const handleNext = () => {
    if (activeStep === 1) {
      if (!basicUserInfo?.company) {
        setShowPaymentDialog(true);
        return;
      }
      setSubmitRequested(true);
      handlePlaceOrder(orderInfo);
    } else {
      setSubmitRequested(true);
    }
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handlePlaceOrder = async (orderInfo: any) => {
    // hope...
    const uid = basicUserInfo?.uid;
    if (!uid) {
      navigate("/login");
      return;
    }
    // end of hope...

    setIsSubmitting(true);
    //console.log("setIsSubmitting:", isSubmitting);
    try {
      const data = { ...orderInfo };
      data.customer_id = basicUserInfo?.uid;
      data.service_type = SERVICE_TYPE.AIRPORT_DROP_OFF;
      if (data.qrcodeFile && isSerializedQrCodeFile(data.qrcodeFile)) {
        data.qrcodeFile = await deserializeQrCodeFile(data.qrcodeFile);
      }
      // data.flight_time = new Date(orderInfo.flight_time).toISOString();
      // data.use_time = new Date(orderInfo.use_time).toISOString();

      //console.log('原始flight_time:', orderInfo.flight_time, orderInfo.use_time);
      // 将 "YYYY-MM-DD HH:mm" 格式的字符串解析为太平洋时间，然后转为 ISO 字符串
      const pstFlightTime = dayjs.tz(orderInfo.flight_time, 'America/Los_Angeles');
      data.flight_time = pstFlightTime.utc().format();

      const pstUseTime = dayjs.tz(orderInfo.use_time, 'America/Los_Angeles');
      data.use_time = pstUseTime.utc().format();
      //console.log('转换后的flight_time:', data.flight_time, data.use_time);

      if (!data.base_fare) {
        data.base_fare = consts.wrongFare;
      }
      //if (!data.tips) {
      //  data.tips = 0;
      //}
      if (!data.base_fare_payment_method && basicUserInfo?.company) {
        data.base_fare_payment_method = consts.PAYMENT_TYPE.ACCOUNT_BILLING;
        data.tips_payment_method = consts.PAYMENT_TYPE.ACCOUNT_BILLING;
      }
      if (isEdit) {
        await dispatch(updateMyOrderById(data));
      } else {
        await dispatch(placeOrder(data));
      }
    } catch (error) {
      // console.error('下单失败:', error);
      dispatch(
        showNotification({
          message: t("errPlaceOrderFailed"),
          type: NotificationType.Error,
        })
      );
    } finally {
      setIsSubmitting(false);
      //console.log("handlePlaceOrder: isSubmitting:", isSubmitting);
    }
  };

  // TODO: 两个操作之间需要重置吗
  useEffect(() => {
    if (orderStatus && orderStatus.status == ORDER_STATUS.SUCCESS) {
      // 只有从已保存订单来的才删除localStorage
      if (location.state?.fromSavedOrder) {
        localStorage.removeItem("savedOrder");
      }
      const orderId = orderStatus?.data?.order_id;
      const orderEmail = encodeURIComponent(orderEmailRef.current || "");
      dispatch(resetOrderStatus());
      navigate(
        orderId
          ? `/success?orderId=${encodeURIComponent(String(orderId))}&email=${orderEmail}`
          : `/success?email=${orderEmail}`,
        { replace: true }
      );
    }
  }, [orderStatus, location.state, dispatch, navigate]);

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleGotoSelect = () => {
    dispatch(resetOrderStatus());
    navigate('/select');
  };

  const handleSaveOrder = async () => {
    if (isSaveOrderClicked) {
      handleGotoSelect();
      return;
    }

    // 保存订单信息到localStorage，只保存一个订单（覆盖之前的）
    const orderToSave = { ...orderInfo } as any;
    if (orderToSave.qrcodeFile instanceof File) {
      orderToSave.qrcodeFile = await serializeQrCodeFile(orderToSave.qrcodeFile);
    }
    orderToSave.uid = basicUserInfo?.uid;
    orderToSave.savedAt = new Date().toISOString();
    orderToSave.id = Date.now().toString();
    localStorage.setItem('savedOrder', JSON.stringify(orderToSave));

    // 显示保存成功提示
    dispatch(
      showNotification({
        message: t("orderSavedSuccess"),
        type: NotificationType.Success,
      })
    );

    // 设置状态为已点击
    setIsSaveOrderClicked(true);
  };

  //const [orderInfo, setOrderInfo] = React.useState(passedOrderInfo || {
  const [orderInfo, setOrderInfo] = React.useState(processedOrderInfo || {
    // 一定不为空，所以PickupForm的初始化只能走这个初始的值
    service_type: SERVICE_TYPE.AIRPORT_DROP_OFF,
    useDate: dayjs().tz('America/Los_Angeles').add(consts.bookingBeforeHours, 'hour'),
    useTime: dayjs().tz('America/Los_Angeles').add(consts.bookingBeforeHours, 'hour'),
    flightDate: dayjs().tz('America/Los_Angeles').add(consts.bookingBeforeHours + 3, 'hour'),
    flightTime: dayjs().tz('America/Los_Angeles').add(consts.bookingBeforeHours + 3, 'hour'),
    language: consts.languages.CHINESE,
    contact_method: consts.contactMethod.PHONE,
    lobby_service: false,
    passengers: 1,
    luggages: 1,
    carry_on: 1,
  });

  useEffect(() => {
    if (basicUserInfo?.uid) {
      dispatch(getFutureOrdersByCustomerId({ customer_id: +basicUserInfo.uid }));
    }
  }, [basicUserInfo]);

function getStepContent(step: number) {
  switch (step) {
    case 0:
      return <DropoffForm 
        parentSubmitCallback={handleFormSubmit} 
        submitRequested={submitRequested} 
        initialValues={activeStep === 0 ? orderInfo : undefined}
        basicUserInfo={basicUserInfo} 
        futureOrders={futureOrders}
        isEdit={isEdit}
        onEditOrder={(orderId) => {
          navigate('/editdropoff', { 
            state: { 
              orderInfo: futureOrders.find(order => order.order_id === orderId) 
            } 
          });
        }}
      />;
    case 1:
      return <Review orderInfo={orderInfo}/>;
    default:
      throw new Error('Unknown step');
  }
}

  const [submitRequested, setSubmitRequested] = useState(false);

  const handleFormSubmit = (successed: boolean, data: any) => {
    // console.log("data:.....", successed, data);
    setSubmitRequested(false);
    if (successed) {
      data.vehicle_type = vehicle;
      orderEmailRef.current = data.email || "";
      setOrderInfo(data);
      setActiveStep(activeStep + 1);
    }
  };

  const handleVehicleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVehicle(+event.target.value);
  };

    // 添加状态来控制 Popover
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

    // 修改状态，添加当前悬停的车型
    const [hoveredVehicleType, setHoveredVehicleType] = React.useState<'miniVan' | 'luxurySUV' | null>(null);

    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>, vehicleType: 'miniVan' | 'luxurySUV') => {
      setAnchorEl(event.currentTarget);
      setHoveredVehicleType(vehicleType);
    };
  
    const handlePopoverClose = () => {
      setAnchorEl(null);
      setHoveredVehicleType(null);
    };

    const [showPaymentDialog, setShowPaymentDialog] = React.useState(false);

    const handlePaymentDialogClose = (confirmed: boolean, paymentMethod?: number) => {
      setShowPaymentDialog(false);
      if (confirmed && paymentMethod !== undefined) {
        const updatedOrderInfo = {
          ...orderInfo,
          base_fare_payment_method: paymentMethod,
          tips_payment_method: paymentMethod,
        };
        setOrderInfo(updatedOrderInfo);
        setSubmitRequested(true);
        handlePlaceOrder(updatedOrderInfo);
      }
    };

    if (!basicUserInfo || basicUserInfo.status === consts.customerStatus.BAN) {
      return <Navigate replace to={"/login"} />;
    }

  return (
    <AppTheme {...props}>
      <Grid
        container
        alignItems="center"
        justifyContent="center"
        sx={{
          height: {
            xs: '100%',
            sm: 'calc(100dvh - var(--template-frame-height, 0px))',
          },
          mt: {
            xs: 4,
            sm: 0,
          },
        }}
      >
        <Grid
          size={12}
          // size={{ 
          //   sm: 12,
          //   md: 12,
          // }}
          sx={{
            display: 'flex',
            flexDirection: 'column',//'row',//
            maxWidth: 1600,
            // maxWidth: '100%',
            width: '100%',
            backgroundColor: { xs: 'transparent', sm: 'background.default' },
            alignItems: 'center',//'start',
            // pt: { xs: 0, sm: 16 },
            px: { xs: 2, sm: 10 },
            gap: { xs: 4, md: 8 },
          }}
        >
          {activeStep === 0 && (
        <Grid
          size={{ 
            sm: 12,
            md: 6,
            lg: 6,
          }}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            maxWidth: '100%',
            width: '100%',
            backgroundColor: { xs: 'transparent', sm: 'background.default' },
            alignItems: 'start',
            // pt: { xs: 0, sm: 16 },
            px: { xs: 2, sm: 10 },
            gap: { xs: 4, md: 8 },
          }}
        >
        <FormGrid size={{ xs: 12, md: 6, lg: 6 }}>
          <Box
            component="img"
            onMouseEnter={(e) => handlePopoverOpen(e, 'miniVan')}
            onMouseLeave={handlePopoverClose}
            sx={{
              // height: 233,
              // width: 350,
              height: 167,
              width: 250,
              maxHeight: { xs: 233, md: 167 },
              maxWidth: { xs: 350, md: 250 },
            }}
            alt="mini van"
            src={miniVanImage}
          >
          </Box>
          <Box
            sx={{
              width: { xs: "50%", md: "50%", lg: "50%" }, // 控制宽度
              display: "flex", // 可选：对齐内容
              alignItems: "center", // 垂直居中
            }}
          >
          <FormControlLabel
            control={
            <Radio
              checked={vehicle === vehicleType.MINIVAN}
              value={vehicleType.MINIVAN}
              onChange={handleVehicleChange}
              name="radio-buttons"
              inputProps={{ 'aria-label': 'Mini Van' }}
            />
            }
            label={t("btnMiniVan")}
          />
          </Box>
        </FormGrid>
        <FormGrid size={{ xs: 12, md:6, lg: 6 }}>
          <Box
            component="img"
            onMouseEnter={(e) => handlePopoverOpen(e, 'luxurySUV')}
            onMouseLeave={handlePopoverClose}
            sx={{
              // height: 233,
              // width: 350,
              height: 167,
              width: 250,
              maxHeight: { xs: 233, md: 167 },
              maxWidth: { xs: 350, md: 250 },
            }}
            alt="Luxury SUV"
            src={luxurySuvImage}
          >
          </Box>
          <Box
            sx={{
              width: { xs: "50%", md: "50%", lg: "50%" }, // 控制宽度
              display: "flex", // 可选：对齐内容
              alignItems: "center", // 垂直居中
            }}
          >
          <FormControlLabel
            control={
            <Radio
              checked={vehicle === vehicleType.LUXURYSUV}
              onChange={handleVehicleChange}
              value={vehicleType.LUXURYSUV}
              name="radio-buttons"
              inputProps={{ 'aria-label': 'Luxury SUV' }}
              disabled={true}
            />
            }
            label={t("btnLuxurySUV")}
          />
          </Box>
          <Popover
            sx={{
              pointerEvents: 'none',
              '& .MuiPopover-paper': {
                right: '20px !important',
                left: 'auto !important',
                position: 'fixed',
                width: '30%',
                minWidth: '300px',
              }
            }}
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'center',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'center',
              horizontal: 'left',
            }}
            onClose={handlePopoverClose}
            disableRestoreFocus
          >
            <Paper sx={{ p: 3, bgcolor: 'black', color: 'white' }}>
              <Typography variant="h4" gutterBottom sx={{ 
                color: '#DAA520',  // 金色
                fontWeight: 'bold',
                mb: 2
              }}>
                {t(hoveredVehicleType === 'miniVan' ? "labelMiniVan" : "labelLuxurySUV")}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {t(hoveredVehicleType === 'miniVan' 
                  ? "descMiniVan" 
                  : "descLuxurySUV")}
              </Typography>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t("labelVehicle")}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                {hoveredVehicleType === 'miniVan' ? (
                  <>
                    <Typography>• {t("descMiniVehicle")}</Typography>
                  </>
                ) : (
                  <>
                    <Typography>• Chevy Suburban</Typography>
                    <Typography>• GMC Yukon XL</Typography>
                  </>
                )}
              </Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t("labelFeatures")}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Typography>• {t("descFeatures")}</Typography>
              </Box>
            </Paper>
          </Popover>
        </FormGrid>
        </Grid>
          )}
        <Grid
          size={{ 
            sm: 12, 
            md: 6,//7, 
            lg: 6,//8 
          }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '100%',
            width: '100%',
            // backgroundColor: { xs: 'transparent', sm: 'background.default' },
            backgroundColor: { xs: 'divider', sm: 'divider' },
            alignItems: 'start',
            // pt: { xs: 0, sm: 16 },
            px: { xs: 2, sm: 10 },
            gap: { xs: 4, md: 8 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: { sm: 'space-between', md: 'flex-end' },
              alignItems: 'center',
              width: '100%',
              maxWidth: { sm: '100%', md: 600 },
            }}
          >
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                flexGrow: 1,
              }}
            >
              <Stepper
                id="desktop-stepper"
                activeStep={activeStep}
                sx={{ width: '100%', height: 40 }}
              >
                {steps.map((label) => (
                  <Step
                    sx={{ 
                      // ':first-child': { pl: 0 }, 
                      ':last-child': { pr: 0 } 
                    }}
                    key={label}
                  >
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              width: '100%',
              maxWidth: { sm: '100%', md: 600 },
              // maxHeight: { sm: '95%', md: 720 },
              //maxHeight: '720px',
              gap: { xs: 5, md: 'none' },
            }}
          >
            <Stepper
              id="mobile-stepper"
              activeStep={activeStep}
              alternativeLabel
              sx={{ display: { sm: 'flex', md: 'none' } }}
            >
              {steps.map((label) => (
                <Step
                  sx={{
                    // ':first-child': { pl: 0 },
                    // ':last-child': { pr: 0 },
                    '& .MuiStepConnector-root': { top: { xs: 6, sm: 12 } },
                  }}
                  key={label}
                >
                  <StepLabel
                    sx={{ '.MuiStepLabel-labelContainer': { maxWidth: '70px' } }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
            <React.Fragment>
              {getStepContent(activeStep)}
              <Box
                sx={[
                  {
                    display: 'flex',
                    flexDirection: { xs: 'column-reverse', sm: 'row' },
                    alignItems: 'end',
                    flexGrow: 1,
                    gap: 1,
                    pb: { xs: 12, sm: 0 },
                    mt: { xs: 2, sm: 0 },
                    mb: '60px',
                  },
                  { justifyContent: 'space-between' }
                ]}
              >
                <Button
                  startIcon={<ChevronLeftRoundedIcon />}
                  onClick={activeStep === 0 ? handleGotoSelect : handleBack}
                  variant="text"
                  sx={{ display: { xs: 'none', sm: 'flex' } }}
                >
                  {activeStep === 0 ? t('btnBack') : t('previous')}
                </Button>
                {activeStep !== 0 && (
                  <Button
                    startIcon={<ChevronLeftRoundedIcon />}
                    onClick={handleBack}
                    variant="outlined"
                    disabled={isSubmitting}
                    fullWidth
                    sx={{ display: { xs: 'flex', sm: 'none' } }}
                  >
                    {t('previous')}
                  </Button>
                )}
                {activeStep === steps.length - 1 && !basicUserInfo?.is_guest && !isEdit && (
                  <Button
                    variant="outlined"
                    onClick={handleSaveOrder}
                    sx={{ 
                      display: { xs: 'none', sm: 'flex' },
                      mx: 1
                    }}
                  >
                    {isSaveOrderClicked ? t('btnClose') : t('btnSaveOrder')}
                  </Button>
                )}
                <Button
                  variant="contained"
                  endIcon={<ChevronRightRoundedIcon />}
                  onClick={handleNext}
                  disabled={isSubmitting}
                  sx={{ width: { xs: '100%', sm: 'fit-content' } }}
                >
                    {activeStep === steps.length - 1
                      ? (isSubmitting
                        ? t('submitting')
                        //: (isEdit ? t('editOrder') : t('placeOrder')))
                        : t('placeOrder'))
                      : t('next')}
                </Button>
              </Box>
            </React.Fragment>
          </Box>
        </Grid>
        {/** end of top */}
        </Grid>
      </Grid>
      <PaymentMethodDialog
        open={showPaymentDialog}
        onClose={handlePaymentDialogClose}
        defaultPaymentMethod={orderInfo.base_fare_payment_method}
      />
      <Backdrop
        open={isSubmitting}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          backgroundColor: 'rgba(255, 255, 255, 0.75)',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            width: { xs: '90%', sm: 560 },
            maxWidth: 620,
            p: { xs: 3, sm: 4 },
            textAlign: 'center',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box sx={{ display: 'inline-flex', gap: 0.5 }}>
              {[0, 1, 2].map((dot) => (
                <Box
                  key={dot}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: '#00c853',
                    animation: `${dotPulse} 1s ${dot * 0.15}s infinite ease-in-out`,
                  }}
                />
              ))}
            </Box>
            <Typography variant="h6" sx={{ color: 'primary.main' }}>
              {t('processing')}
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {t('processingRequest')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('processingPleaseWait')}
          </Typography>
        </Paper>
      </Backdrop>
    </AppTheme>
  );
}

export default DropOff;
